import {
  IconSettings, IconBuildingBank, IconLayoutDashboard,
  IconClipboardCheck, IconReportAnalytics,
} from '@tabler/icons-react';
import { Avatar, Box, Center, Group, Menu, Tooltip, ActionIcon } from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../images/Logo_Black_Smooth.png';
import classes from './Header.module.css';

export function Header({ user, isAuthenticated, userRoles, userIsRiskManager, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Dashboard is the merged Service Desk + Risk Landscape page. /, /dashboard
  // and /landscape (alias) all resolve to the same component, so each path
  // counts as the active Dashboard tab.
  const isDashboardActive =
    location.pathname === '/' ||
    location.pathname === '/dashboard' ||
    location.pathname === '/landscape';

  // Team Members get a simplified nav
  const isManager = userIsRiskManager;

  return (
    <header className={classes.header}>
      <div className={classes.content}>
        {/* Left edge: logo */}
        <button className={classes.edgeButton} aria-label="Home" onClick={() => navigate('/')}>
          <img src={Logo} alt="logo" className={classes.logo} />
        </button>

        {/* Middle: nav */}
        <Group h="100%" gap={0}>
          {isManager ? (
            /* Risk Manager nav — full access */
            <>
              <button
                className={classes.link}
                data-active={isDashboardActive || undefined}
                onClick={() => navigate('/dashboard')}
              >
                <Center inline>
                  <IconLayoutDashboard size={16} style={{ marginRight: 4 }} />
                  <Box component="span">Dashboard</Box>
                </Center>
              </button>

              <button
                className={classes.link}
                data-active={location.pathname.startsWith('/agency') || undefined}
                onClick={() => navigate('/agency')}
              >
                <Center inline>
                  <IconBuildingBank size={16} style={{ marginRight: 4 }} />
                  <Box component="span">Agency</Box>
                </Center>
              </button>

              <button
                className={classes.link}
                data-active={location.pathname === '/assessments' || undefined}
                onClick={() => navigate('/assessments')}
              >
                <Center inline>
                  <IconClipboardCheck size={16} style={{ marginRight: 4 }} />
                  <Box component="span">Assessments</Box>
                </Center>
              </button>

              <button
                className={classes.link}
                data-active={location.pathname === '/reports' || undefined}
                onClick={() => navigate('/reports')}
              >
                <Center inline>
                  <IconReportAnalytics size={16} style={{ marginRight: 4 }} />
                  <Box component="span">Reports</Box>
                </Center>
              </button>
            </>
          ) : (
            /* Team Member nav — simplified */
            <>
              <button
                className={classes.link}
                data-active={location.pathname === '/' || undefined}
                onClick={() => navigate('/')}
              >
                Dashboard
              </button>

              <button
                className={classes.link}
                data-active={location.pathname === '/assessments' || location.pathname.startsWith('/assessments/') || undefined}
                onClick={() => navigate('/assessments')}
              >
                Assessments
              </button>

              <button
                className={classes.link}
                data-active={location.pathname === '/reports' || undefined}
                onClick={() => navigate('/reports')}
              >
                Reports
              </button>
            </>
          )}
        </Group>

        {/* Right edge: avatar menu */}
        {isAuthenticated && user && (
          <Menu>
            <Menu.Target>
              <Tooltip label={`Role: ${userRoles.join(', ') || 'User'}`}>
                <ActionIcon variant="transparent" radius="xl" size={44} className={classes.edgeIcon}>
                  <Avatar src={user.picture} alt={user.name || 'User'} radius="xl" size={44} />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item disabled>Hello, {user?.name || 'User'}</Menu.Item>
              <Menu.Divider />
              <Menu.Item leftSection={<IconSettings size={16} />} disabled>
                Settings
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item onClick={onLogout}>Logout</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </div>
    </header>
  );
}
